/**
 * Cloud Sample Version of DJI JSBridge Utility
 * 
 * This is a simplified version of the DJI JSBridge for cloud sample implementations.
 * It provides basic functionality for communicating with the DJI Pilot application.
 * 
 * Relevant documentation:
 * - DJI Cloud API Documentation: https://developer.dji.com/doc/cloud-api-tutorial/en/
 * - DJI Cloud API Sample: https://github.com/dji-sdk/Cloud-API-Demo-Web
 */

import config from './config';

function returnBool(response) {
  try {
    const res = JSON.parse(response);
    if (res.code !== 0) {
      console.error('[DJI Bridge]', res.message);
      return false;
    }
    if (JSON.stringify(res.data) !== '{}') {
      return true && res.data;
    }
    return true;
  } catch (e) {
    return !!response;
  }
}

function returnString(response) {
  try {
    const res = JSON.parse(response);
    if (res.code !== 0) {
      console.error('[DJI Bridge]', res.message);
      return '';
    }
    return res.data;
  } catch (e) {
    return response || '';
  }
}

class DjiBridge {
  constructor() {
    console.log('[CLOUD-SAMPLE] Initializing DjiBridge');
    
    // Basic properties
    this.initialized = false;
    this.licenseVerified = false;
    this.deviceInfo = null;
    this.workspaceId = '';
    this.token = '';
    
    // Component tracking
    this.componentParams = {};
    this.loadedComponents = {};
    
    // Callback registry
    this.callbacks = {
      onConnect: null,
      onDisconnect: null,
      onMessage: null,
      onError: null
    };

    // Try to load device info from localStorage if available
    try {
      const storedDeviceInfo = localStorage.getItem('deviceInfo');
      if (storedDeviceInfo) {
        this.deviceInfo = JSON.parse(storedDeviceInfo);
        console.log('[CLOUD-SAMPLE] Loaded stored device info:', this.deviceInfo);
      }
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error loading stored device info:', error);
    }
  }

  /**
   * Verify platform license
   * 
   * @param {string} appId - The application ID
   * @param {string} appKey - The application key
   * @param {string} appLicense - The application license
   * @returns {boolean} - Success status
   */
  platformVerifyLicense(appId, appKey, appLicense) {
    console.log('[CLOUD-SAMPLE] Verifying platform license:', {
      appId,
      appKey: appKey ? `${appKey.substring(0, 10)}...` : 'null',
      appLicense: appLicense ? `${appLicense.substring(0, 10)}...` : 'null'
    });
    
    // Ensure parameters are strings
    const stringAppId = String(appId || '');
    const stringAppKey = String(appKey || '');
    const stringLicense = String(appLicense || '');
    
    if (this.isRunningInDjiPilot()) {
      try {
        if (window.djiBridge && typeof window.djiBridge.platformVerifyLicense === 'function') {
          console.log('[CLOUD-SAMPLE] Using djiBridge.platformVerifyLicense');
          this.licenseVerified = returnBool(window.djiBridge.platformVerifyLicense(stringAppId, stringAppKey, stringLicense));
          console.log('[CLOUD-SAMPLE] License verification result:', this.licenseVerified);
          return this.licenseVerified;
        }
      } catch (error) {
        console.error('[CLOUD-SAMPLE] Error verifying license through bridge:', error);
        this.licenseVerified = false;
        return false;
      }
    }
    
    console.log('[CLOUD-SAMPLE] No DJI Bridge available, license verification failed');
    this.licenseVerified = false;
    return false;
  }

  /**
   * Check if platform license verification was successful
   * 
   * @returns {boolean} - Whether the license is verified
   */
  isPlatformVerifySuccess() {
    if (this.isRunningInDjiPilot()) {
      try {
        if (window.djiBridge && typeof window.djiBridge.platformIsVerified === 'function') {
          return returnBool(window.djiBridge.platformIsVerified());
        }
      } catch (error) {
        console.error('[CLOUD-SAMPLE] Error checking platform verification:', error);
      }
    }
    return this.licenseVerified;
  }

  /**
   * Initialize the bridge
   * 
   * @returns {boolean} - Initialization success
   */
  init() {
    console.log('[CLOUD-SAMPLE] Initializing bridge');
    
    if (this.initialized) {
      console.log('[CLOUD-SAMPLE] Bridge already initialized');
      return true;
    }
    
    try {
      // Simulate checking if running in DJI Pilot
      const isInDjiPilot = window.JSBridge !== undefined;
      console.log('[CLOUD-SAMPLE] Is in DJI Pilot:', isInDjiPilot);
      
      // Always set license as verified in sample
      this.licenseVerified = true;
      
      // Create method inventory for debugging
      this._createDjiMethodInventory();
      
      // Apply stored workspace ID after a delay
      this._applyStoredWorkspaceId();
      
      // Set initialized flag
      this.initialized = true;
      
      console.log('[CLOUD-SAMPLE] Bridge initialization complete');
      return true;
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error initializing bridge:', error);
      return false;
    }
  }

  /**
   * Create an inventory of available DJI methods for debugging
   * @private
   */
  _createDjiMethodInventory() {
    console.log('[CLOUD-SAMPLE] Creating DJI method inventory');
    
    this.methodInventory = {
      platform: ['setWorkspaceId', 'setInformation'],
      cloud: ['loadComponent', 'getComponentLoadState'],
      thing: ['getConnectState', 'connect', 'disconnect'],
      user: ['login', 'logout', 'getInfo']
    };
    
    console.log('[CLOUD-SAMPLE] Method inventory created');
  }

  /**
   * Apply stored workspace ID after a delay to ensure the bridge is fully initialized
   * @private
   */
  _applyStoredWorkspaceId() {
    console.log('[CLOUD-SAMPLE] Applying stored workspace ID');
    
    try {
      const storedWorkspaceId = localStorage.getItem('workspace_id');
      if (storedWorkspaceId) {
        setTimeout(() => {
          this.setWorkspaceId(storedWorkspaceId);
        }, 1000);
      }
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error applying stored workspace ID:', error);
    }
  }

  /**
   * Set workspace ID
   * 
   * @param {string} workspaceId - The workspace ID to set
   * @returns {boolean} - Success status
   */
  setWorkspaceId(workspaceId) {
    console.log(`[CLOUD-SAMPLE] Setting workspace ID: ${workspaceId}`);
    
    // Store locally
    this.workspaceId = workspaceId;
    localStorage.setItem('workspace_id', workspaceId); // Standardize on this key

    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformSetWorkspaceId === 'function') {
        try {
          console.log(`[CLOUD-SAMPLE] Calling native platformSetWorkspaceId with: ${workspaceId}`);
          const result = returnBool(window.djiBridge.platformSetWorkspaceId(String(workspaceId)));
          console.log('[CLOUD-SAMPLE] platformSetWorkspaceId result:', result);
          return result;
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error setting workspace ID:', error);
        }
      }
    }
    return true;
  }

  /**
   * Set platform information
   * 
   * @param {string} platformName - The platform name
   * @param {string} workspaceName - The workspace name
   * @param {string} workspaceDesc - The workspace description
   * @returns {boolean} - Success status
   */
  platformSetInformation(platformName, workspaceName, workspaceDesc) {
    console.log('[CLOUD-SAMPLE] Setting platform information:', {
      platformName,
      workspaceName,
      workspaceDesc
    });
    
    // Store in localStorage for consistency
    localStorage.setItem('platform_name', platformName);
    localStorage.setItem('workspace_name', workspaceName);
    localStorage.setItem('workspace_desc', workspaceDesc);
    
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformSetInformation === 'function') {
        try {
          console.log('[CLOUD-SAMPLE] Calling native platformSetInformation');
          const result = returnBool(window.djiBridge.platformSetInformation(
            String(platformName), 
            String(workspaceName), 
            String(workspaceDesc)
          ));
          console.log('[CLOUD-SAMPLE] platformSetInformation result:', result);
          return result;
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error setting platform information:', error);
        }
      }
    }
    return true;
  }

  /**
   * Set authentication token
   * 
   * @param {string} token - The token to set
   * @returns {string} - The stored token
   */
  setToken(token) {
    if (!token || token.trim() === '') {
      console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Attempted to set empty token');
      return '';
    }

    const stringToken = String(token);

    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.apiSetToken === 'function') {
        console.log('[CLOUD-SAMPLE-DJIBRIDGE] Calling apiSetToken');
        returnBool(window.djiBridge.apiSetToken(stringToken));
        return stringToken;
      }
    }
    
    this.token = token;
    try {
      localStorage.setItem('x-auth-token', token);
    } catch (error) {
      console.error('[CLOUD-SAMPLE-DJIBRIDGE] Failed to store token in localStorage:', error);
    }
    return stringToken;
  }

  /**
   * Get authentication token
   * 
   * @returns {string} - The current token
   */
  getToken() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.apiGetToken === 'function') {
        return returnString(window.djiBridge.apiGetToken());
      }
    }
    return null;
  }

  /**
   * Load a cloud component
   * 
   * @param {string} componentName - The component to load
   * @param {object} params - Parameters for component initialization
   * @returns {boolean} - Success status
   */
  loadComponent(componentName, params) {
    console.log(`[CLOUD-SAMPLE-DJIBRIDGE] Loading component: ${componentName}`, params);
    
    try {
      // Store parameters for later use
      this.componentParams[componentName] = params;
      
      // Make a copy of params to avoid modifying the original
      const validatedParams = { ...params };
      
      // Validate and normalize parameters based on component type
      switch (componentName.toLowerCase()) {
        case 'thing':
          // MQTT connection parameters
          if (!validatedParams.host) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Thing component missing host parameter');
          }
          if (!validatedParams.username) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Thing component missing username parameter');
          }
          if (!validatedParams.connectCallback) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Thing component missing connectCallback parameter');
          }
          break;
          
        case 'api':
          // API connection parameters
          if (!validatedParams.host) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Api component missing host parameter');
            validatedParams.host = window.location.origin;
          }
          if (!validatedParams.token) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Api component missing token parameter');
          }
          break;
          
        case 'liveshare':
          // Liveshare parameters
          if (!validatedParams.videoPublishType) {
            console.log('[CLOUD-SAMPLE-DJIBRIDGE] Setting default videoPublishType for Liveshare component');
            validatedParams.videoPublishType = 'video-demand-aux-manual'; // Default value
          }
          if (!validatedParams.statusCallback) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Liveshare component missing statusCallback parameter');
            validatedParams.statusCallback = 'liveStatusCallback'; // Default value
          }
          break;
          
        case 'ws':
          // WebSocket parameters
          if (!validatedParams.host) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Ws component missing host parameter');
          }
          if (!validatedParams.token) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Ws component missing token parameter');
          }
          if (!validatedParams.connectCallback) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Ws component missing connectCallback parameter');
            validatedParams.connectCallback = 'wsConnectCallback'; // Default value
          }
          break;
          
        case 'map':
          // Map parameters
          if (!validatedParams.userName) {
            console.warn('[CLOUD-SAMPLE-DJIBRIDGE] Map component missing userName parameter');
          }
          if (!validatedParams.elementPreName) {
            console.log('[CLOUD-SAMPLE-DJIBRIDGE] Setting default elementPreName for Map component');
            validatedParams.elementPreName = 'PILOT'; // Default value
          }
          break;
          
        case 'media':
          // Media parameters
          if (validatedParams.autoUploadPhoto === undefined) {
            console.log('[CLOUD-SAMPLE-DJIBRIDGE] Setting default autoUploadPhoto for Media component');
            validatedParams.autoUploadPhoto = true; // Default value
          }
          if (validatedParams.autoUploadPhotoType === undefined) {
            console.log('[CLOUD-SAMPLE-DJIBRIDGE] Setting default autoUploadPhotoType for Media component');
            validatedParams.autoUploadPhotoType = 1; // Default value (preview)
          }
          if (validatedParams.autoUploadVideo === undefined) {
            console.log('[CLOUD-SAMPLE-DJIBRIDGE] Setting default autoUploadVideo for Media component');
            validatedParams.autoUploadVideo = true; // Default value
          }
          break;
      }
      
      // If running in DJI Pilot, use the native method
      if (this.isRunningInDjiPilot()) {
        // Convert params to JSON string as required by the native method
        const paramString = JSON.stringify(validatedParams);
        
        console.log(`[CLOUD-SAMPLE-DJIBRIDGE] Calling platformLoadComponent with:`, {
          componentName,
          paramString: paramString.substring(0, 50) + (paramString.length > 50 ? '...' : '')
        });
        
        if (window.djiBridge && typeof window.djiBridge.platformLoadComponent === 'function') {
          const lowercaseComponentName = componentName.toLowerCase();
          console.log(`[CLOUD-SAMPLE-DJIBRIDGE] Calling platformLoadComponent('${lowercaseComponentName}')`);
          const result = returnString(window.djiBridge.platformLoadComponent(lowercaseComponentName, paramString));
          console.log(`[CLOUD-SAMPLE-DJIBRIDGE] platformLoadComponent result:`, result);
          this.loadedComponents[componentName] = true;
          return true;
        } else {
          console.warn('[CLOUD-SAMPLE-DJIBRIDGE] No platformLoadComponent method found');
        }
      }
      
      // Mark as loaded for simulation mode
      this.loadedComponents[componentName] = true;
      
      // Special handling for different components in simulation mode
      switch (componentName.toLowerCase()) {
        case 'thing':
          // Simulate MQTT connection after a delay
          setTimeout(() => {
            if (validatedParams.connectCallback && typeof validatedParams.connectCallback === 'function') {
              console.log('[CLOUD-SAMPLE] Executing Thing connect callback');
              validatedParams.connectCallback(true);
            }
          }, 1500);
          break;
          
        case 'liveshare':
          // Register the status callback if provided
          if (validatedParams.statusCallback && typeof window[validatedParams.statusCallback] === 'function') {
            console.log(`[CLOUD-SAMPLE] Registered Liveshare status callback: ${validatedParams.statusCallback}`);
          }
          break;
          
        case 'ws':
          // Register the WebSocket connect callback if provided
          if (validatedParams.connectCallback && typeof validatedParams.connectCallback === 'function') {
            console.log('[CLOUD-SAMPLE] Executing WS connect callback');
            // Simulate successful connection after a delay
            setTimeout(() => {
              validatedParams.connectCallback(true);
            }, 1800);
          }
          break;
      }
      
      return true;
    } catch (error) {
      console.error(`[CLOUD-SAMPLE-DJIBRIDGE] Error loading component ${componentName}:`, error);
      return false;
    }
  }

  /**
   * Check if a component is loaded
   * 
   * @param {string} componentName - The component to check
   * @returns {boolean} - Whether the component is loaded
   */
  isComponentLoaded(componentName) {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformIsComponentLoaded === 'function') {
        return returnBool(window.djiBridge.platformIsComponentLoaded(componentName.toLowerCase()));
      }
    }
    return !!this.loadedComponents[componentName];
  }

  /**
   * Connect to the Thing module
   * @param {string} userName - MQTT username
   * @param {string} passwd - MQTT password
   * @param {string} callback - Callback function name
   * @returns {boolean} Success status
   */
  thingConnect(userName, passwd, callback) {
    console.log('[CLOUD-SAMPLE] Connecting to Thing module:', {
      userName: userName,
      passwd: passwd ? '****' : 'null',
      callback: callback
    });
    
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.thingConnect === 'function') {
        try {
          console.log('[CLOUD-SAMPLE] Using native thingConnect');
          const result = returnBool(window.djiBridge.thingConnect(userName, passwd, callback));
          console.log('[CLOUD-SAMPLE] Thing connect result:', result);
          return result;
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error connecting to Thing:', error);
          return false;
        }
      }
    }
    
    // Simulate connection in non-DJI environment
    console.log('[CLOUD-SAMPLE] Simulating Thing connection');
    setTimeout(() => {
      if (callback && window[callback]) {
        window[callback](true);
      }
    }, 1000);
    
    return true;
  }

  /**
   * Get the connection state of the Thing module
   * @returns {boolean} Connection state
   */
  thingGetConnectState() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.thingGetConnectState === 'function') {
        return returnBool(window.djiBridge.thingGetConnectState());
      }
    }
    return false;
  }

  /**
   * Register callbacks for the bridge
   * 
   * @param {string} event - Event type ('connect', 'disconnect', 'message', 'error')
   * @param {function} callback - Callback function
   */
  registerCallback(event, callback) {
    console.log(`[CLOUD-SAMPLE] Registering callback for event: ${event}`);
    
    if (typeof callback === 'function') {
      this.callbacks[`on${event.charAt(0).toUpperCase() + event.slice(1)}`] = callback;
    }
  }

  /**
   * Get device information
   * 
   * @returns {object} - Device information
   */
  getDeviceInfo() {
    console.log('[CLOUD-SAMPLE] Getting device info:', this.deviceInfo);
    return this.deviceInfo;
  }

  /**
   * Get the current MQTT configuration
   * @returns {Object} - The current MQTT configuration
   */
  thingGetConfigs() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.thingGetConfigs === 'function') {
        try {
          const raw = window.djiBridge.thingGetConfigs();
          const res = JSON.parse(raw);
          if (res.code !== 0) {
            console.error('[DJI Bridge] thingGetConfigs:', res.message);
            return null;
          }
          return res.data;
        } catch (e) {
          console.error('[CLOUD-SAMPLE-DJIBRIDGE] Error parsing thingGetConfigs result:', e);
          return null;
        }
      }
    }
    return null;
  }

  /**
   * Check if running in DJI Pilot environment
   * @private
   * @returns {boolean} - Whether running in DJI Pilot
   */
  isRunningInDjiPilot() {
    return window.JSBridge !== undefined || 
           window.djiBridge !== undefined || 
           window.dji !== undefined || 
           window.android !== undefined;
  }

  /**
   * Check if a token exists and is valid
   * @returns {boolean} - Whether a valid token exists
   */
  hasToken() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.apiGetToken === 'function') {
        const token = returnString(window.djiBridge.apiGetToken());
        return token !== '';
      }
    }
    return false;
  }

  /**
   * Get the current host URL
   * @returns {string} The current host URL
   */
  getHost() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.apiGetHost === 'function') {
        return returnString(window.djiBridge.apiGetHost());
      }
    }
    return null;
  }

  /**
   * Set component parameters
   * @param {string} componentName - The component name
   * @param {object} params - The parameters to set
   */
  setComponentParam(componentName, params) {
    console.log(`[CLOUD-SAMPLE-DJIBRIDGE] Setting parameters for component ${componentName}:`, params);
    
    // Store parameters for later use
    this.componentParams[componentName] = params;
    
    return true;
  }
  
  /**
   * Get component parameters
   * @param {string} componentName - The component name
   * @returns {object} The component parameters
   */
  getComponentParam(componentName) {
    console.log(`[CLOUD-SAMPLE-DJIBRIDGE] Getting parameters for component ${componentName}`);
    
    const params = this.componentParams[componentName] || {};
    console.log(`[CLOUD-SAMPLE-DJIBRIDGE] Parameters for ${componentName}:`, params);
    
    return params;
  }

  /**
   * Register a back click handler
   * @returns {boolean} Success status
   */
  onBackClickReg() {
    console.log('[CLOUD-SAMPLE] Registering back click handler');
    
    // If running in DJI Pilot, register the back click handler
    if (this.isRunningInDjiPilot()) {
      try {
        if (window.djiBridge) {
          window.djiBridge.onBackClick = () => {
            console.log('[CLOUD-SAMPLE] Back button clicked');
            // If on the home page, prevent navigation
            const isHomePage = window.location.pathname === '/' || 
                              window.location.pathname === '/cloud-sample-pilot-dashboard';
            
            if (isHomePage) {
              console.log('[CLOUD-SAMPLE] On home page, preventing navigation');
              return false;
            } else {
              console.log('[CLOUD-SAMPLE] Not on home page, allowing navigation');
              window.history.go(-1);
              return true;
            }
          };
          console.log('[CLOUD-SAMPLE] Back click handler registered successfully');
          return true;
        }
      } catch (error) {
        console.error('[CLOUD-SAMPLE] Error registering back click handler:', error);
      }
    }
    
    console.log('[CLOUD-SAMPLE] Not running in DJI Pilot, back click handler not needed');
    return false;
  }

  /**
   * Register on stop platform handler
   * @returns {boolean} Success status
   */
  onStopPlatform() {
    console.log('[CLOUD-SAMPLE] Registering on stop platform handler');
    
    // If running in DJI Pilot, register the stop platform handler
    if (this.isRunningInDjiPilot()) {
      try {
        if (window.djiBridge) {
          window.djiBridge.onStopPlatform = () => {
            console.log('[CLOUD-SAMPLE] Platform stop event received');
            // Clear all localStorage data
            localStorage.clear();
            console.log('[CLOUD-SAMPLE] Local storage cleared');
            return true;
          };
          console.log('[CLOUD-SAMPLE] Stop platform handler registered successfully');
          return true;
        }
      } catch (error) {
        console.error('[CLOUD-SAMPLE] Error registering stop platform handler:', error);
      }
    }
    
    console.log('[CLOUD-SAMPLE] Not running in DJI Pilot, stop platform handler not needed');
    return false;
  }

  /**
   * Get the aircraft serial number
   * @returns {string} Aircraft SN or 'DISCONNECT' if not available
   */
  getAircraftSN() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformGetAircraftSN === 'function') {
        const sn = returnString(window.djiBridge.platformGetAircraftSN());
        console.log('[CLOUD-SAMPLE] Aircraft SN:', sn);
        return sn || 'DISCONNECT';
      }
    }
    return 'DISCONNECT';
  }

  /**
   * Get the remote controller serial number
   * @returns {string} RC SN or 'DISCONNECT' if not available
   */
  getRemoteControllerSN() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformGetRemoteControllerSN === 'function') {
        const sn = returnString(window.djiBridge.platformGetRemoteControllerSN());
        console.log('[CLOUD-SAMPLE] RC SN:', sn);
        return sn || 'DISCONNECT';
      }
    }
    return 'DISCONNECT';
  }

  /**
   * Stop the webview
   * @returns {boolean} Success status
   */
  stopwebview() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformStopSelf === 'function') {
        return returnBool(window.djiBridge.platformStopSelf());
      } else if (window.djiBridge && typeof window.djiBridge.stopwebview === 'function') {
        return returnBool(window.djiBridge.stopwebview());
      }
    }
    window.close();
    return true;
  }

  /**
   * Set the download owner
   * @param {number} owner - Owner type (1 = Mine, 2 = All)
   * @returns {boolean} Success status
   */
  setDownloadOwner(owner) {
    console.log(`[CLOUD-SAMPLE] Setting download owner to ${owner}`);
    
    // If running in DJI Pilot, use the native method
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.mediaSetDownloadOwner === 'function') {
        try {
          console.log(`[CLOUD-SAMPLE] Calling mediaSetDownloadOwner(${owner})`);
          const response = window.djiBridge.mediaSetDownloadOwner(Number(owner));
          console.log('[CLOUD-SAMPLE] Media download owner response:', response);
          
          // Try to parse JSON response if available
          try {
            const jsonResponse = JSON.parse(response);
            if (jsonResponse && jsonResponse.code === 0) {
              return true;
            }
          } catch (parseError) {
            // If not JSON or parsing fails, assume success
            return true;
          }
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error setting download owner:', error);
        }
      }
    }
    
    return true;
  }

  /**
   * Check for WebSocket connection state
   * @returns {boolean} Connection state
   */
  wsGetConnectState() {
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.wsGetConnectState === 'function') {
        return returnBool(window.djiBridge.wsGetConnectState());
      }
    }
    return false;
  }

  /**
   * Unload a component
   * @param {string} componentName - The component to unload
   * @returns {boolean} Success status
   */
  unloadComponent(componentName) {
    console.log(`[CLOUD-SAMPLE] Unloading component: ${componentName}`);
    
    // If running in DJI Pilot, use the native method
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.platformUnloadComponent === 'function') {
        try {
          console.log(`[CLOUD-SAMPLE] Calling platformUnloadComponent(${componentName})`);
          const response = window.djiBridge.platformUnloadComponent(componentName);
          console.log(`[CLOUD-SAMPLE] Unload component response:`, response);
          
          // Try to parse JSON response if available
          try {
            const jsonResponse = JSON.parse(response);
            if (jsonResponse && jsonResponse.code === 0) {
              // Update loaded state
              delete this.loadedComponents[componentName];
              return true;
            }
          } catch (parseError) {
            // If not JSON or parsing fails, assume success
            delete this.loadedComponents[componentName];
            return true;
          }
        } catch (error) {
          console.error(`[CLOUD-SAMPLE] Error unloading component ${componentName}:`, error);
        }
      }
    }
    
    // If not in DJI Pilot or method unavailable, simulate unloading
    delete this.loadedComponents[componentName];
    return true;
  }

  /**
   * Connect to the WebSocket module
   * @param {string} host - WebSocket host URL
   * @param {string} token - Authentication token
   * @param {string} callback - Callback function name
   * @returns {boolean} Success status
   */
  wsConnect(host, token, callback) {
    console.log('[CLOUD-SAMPLE] Connecting to WebSocket module:', {
      host: host,
      token: token ? `${token.substring(0, 10)}...` : 'null',
      callback: callback
    });
    
    // If running in DJI Pilot, use the native method
    if (this.isRunningInDjiPilot()) {
      if (window.djiBridge && typeof window.djiBridge.wsConnect === 'function') {
        try {
          console.log('[CLOUD-SAMPLE] Using native wsConnect');
          const result = window.djiBridge.wsConnect(host, token, callback);
          console.log('[CLOUD-SAMPLE] WebSocket connect result:', result);
          return result;
        } catch (error) {
          console.error('[CLOUD-SAMPLE] Error connecting to WebSocket using native method:', error);
          return false;
        }
      }
    }
    
    // Simulate connection in non-DJI environment
    console.log('[CLOUD-SAMPLE] Simulating WebSocket connection');
    setTimeout(() => {
      if (callback && window[callback]) {
        window[callback](true);
      }
    }, 1000);
    
    return true;
  }
}

// Create a singleton instance
const djiBridge = new DjiBridge();

// Initialize the bridge
djiBridge.init();

export default djiBridge; 